import clsx from 'clsx';
import { useCallback, useEffect } from 'react';
import ReactTooltip from 'react-tooltip';
import type { State as UserState } from '../../../context/user-context';
import {
  AttendanceStatus as AttendanceStatusEnum,
  type Meeting,
  MeetingCategory,
} from '../../../graphql/types';
import { DateHelpers } from '../../../helpers/date-helpers';
import { AttendanceStatus } from '../../meetings/components/attendance-status';
import { useAttendance } from '../../meetings/hooks/use-attendance';
import { useIsShared } from '../hooks/use-report-location';
import BtnDeleteMeeting from './btn-delete-meeting';
import { BtnEmailMeeting } from './btn-email-meeting';
import BtnReuploadMeeting from './btn-reupload-meeting';

interface MeetingStatsProps {
  loading: {
    meetingLoading: boolean;
    Events: boolean;
    Groups: boolean;
    'Heat Map': boolean;
    'Hotel Budget': boolean;
    'Last Night Summary': boolean;
    'Monthly Performance': boolean;
    'Sold, ADR, Rev': boolean;
    'Detail Table': boolean;
    'RM Recs': boolean;
    Tonight: boolean;
    'Last Night': boolean;
  };
  meeting?: Meeting;
  user?: UserState;
  isAdmin?: boolean;
  setMeeting?: (meeting: Meeting) => void;
}

const MeetingStats = ({ loading, meeting, user, isAdmin, setMeeting }: MeetingStatsProps) => {
  const { isShared } = useIsShared();
  const { updateAttendanceStatus, loading: attendanceLoading } = useAttendance({
    meetingId: meeting?.id || '',
    onMeetingUpdate: setMeeting,
  });

  // Wrapper function to pass current meeting data for optimistic updates
  const handleAttendanceChange = useCallback(
    (status: AttendanceStatusEnum) => {
      updateAttendanceStatus(status, meeting);
    },
    [updateAttendanceStatus, meeting],
  );

  useEffect(() => {
    if (loading.meetingLoading === false) {
      ReactTooltip.rebuild();
    }
  }, [loading.meetingLoading, meeting]);

  const baseBtnClass =
    'relative inline-flex items-center mx-1 text-sm font-medium text-gray-500 focus:z-10';

  const loadingList = Object.entries(loading)
    .filter(([, value]) => value === true)
    .map(([key]) => key);

  const Loading = () => {
    return (
      <div className='border-t border-gray-200 bg-gray-100 grid grid-cols-1 divide-y divide-gray-200'>
        <div className='px-6 py-2 text-sm font-medium text-center'>
          <span className='text-gray-600'>Checking for existing meeting...</span>
        </div>
      </div>
    );
  };

  if (loading.meetingLoading) {
    return <Loading />;
  }

  if (meeting?.meetingDate && meeting.createdAt) {
    let gridClass = 'sm:grid-cols-4';

    if (meeting.category === MeetingCategory.Weekly) {
      if (isShared) {
        gridClass = 'sm:grid-cols-4';
      }
      if (!isShared) {
        gridClass = 'sm:grid-cols-5';
      }
    } else if (isShared) {
      gridClass = 'sm:grid-cols-3';
    }

    return (
      <div
        className={clsx(
          'border-t border-gray-200 bg-gray-100 grid grid-cols-1 divide-y divide-gray-200 sm:divide-y-0 sm:divide-x',
          gridClass,
        )}
      >
        <div className='px-6 py-2 text-sm font-medium text-center flex items-center justify-center'>
          <span className='text-gray-600'>For: </span>
          <span className='text-gray-900'>{DateHelpers.displayDate(meeting.meetingDate)}</span>
        </div>
        <div className='px-6 py-2 text-sm font-medium text-center flex items-center justify-center'>
          <span className='text-gray-600'>By: </span>
          <span className='text-gray-900'>{user?.name}</span>
        </div>
        <div className='px-6 py-2 text-sm font-medium text-center flex items-center justify-center'>
          <span className='text-gray-600'>Created: </span>
          <span className='text-gray-900'>{DateHelpers.displayDate(meeting.meetingDate)}</span>
        </div>
        {meeting.category === MeetingCategory.Weekly ? (
          <div className='px-6 py-2 text-sm font-medium text-center flex items-center justify-center'>
            <span className='text-gray-600'>Attendance: </span>
            <div className='inline-flex items-center'>
              <AttendanceStatus
                currentStatus={meeting.attendanceStatus || AttendanceStatusEnum.Attended}
                disabled={isShared || attendanceLoading}
                onStatusChange={handleAttendanceChange}
                meetingCategory={meeting.category}
              />
            </div>
          </div>
        ) : null}
        {!isShared && (
          <div className='px-6 py-2 text-sm font-medium text-center flex items-center justify-center'>
            <BtnEmailMeeting btnClass={baseBtnClass} loading={loadingList} />
            <BtnReuploadMeeting btnClass={baseBtnClass} loading={loadingList} />
            {isAdmin && <BtnDeleteMeeting btnClass={baseBtnClass} />}
          </div>
        )}
      </div>
    );
  } else {
    return (
      <div className='border-t border-gray-200 bg-gray-100 grid grid-cols-1 divide-y divide-gray-200 sm:grid-cols-4 sm:divide-y-0 sm:divide-x'>
        <div className='px-6 py-2 text-sm font-medium text-center flex items-center justify-center'>
          <span className='text-gray-600'>For: </span>
          <span className='text-gray-900'></span>
        </div>
        <div className='px-6 py-2 text-sm font-medium text-center flex items-center justify-center'>
          <span className='text-gray-600'>By: </span>
          <span className='text-gray-900'></span>
        </div>
        <div className='px-6 py-2 text-sm font-medium text-center'>
          <span className='text-gray-600'>Attendance: </span>
          <span className='text-gray-900'></span>
        </div>
        {!isShared && (
          <div className='px-6 py-2 text-sm font-medium text-center flex items-center justify-center'>
            <BtnEmailMeeting btnClass={baseBtnClass} loading={loadingList} />
          </div>
        )}
      </div>
    );
  }
};

export default MeetingStats;
